import { useState } from "react";
import FieldHint from "./FieldHint";
import type { SpecialScoringConfig, StandingsRulesConfig, SuperTiebreakRange } from "../lib/api";
import {
  DEFAULT_CHOSEN_SCORE,
  EXAMPLE_STB_RANGES,
  MAX_STB_RANGES,
  exampleHeadline,
  isDefaultSpecialScoring,
  normalizeSpecialScoring,
  specialScoringError,
  specialScoringExample,
} from "../lib/specialScoring";
import {
  DEFAULT_STANDINGS_POINTS,
  itfStandingsRules,
  pointsLeagueStandingsRules,
  standingsRulesKind,
} from "../lib/standingsRules";

/** Tarjeta «Puntuación especial» del Paso Formato (maqueta aprobada
 * Formato-Puntuacion.dc.html). Lo marcado por default es cómo cuenta hoy.
 *
 * - scope "full": round robin / grupos (W.O., DEF, puntos y avanzadas).
 * - scope "exceptions": escalera (solo W.O./DEF/RET y si cuentan en los %).
 * Los «Puntos en la tabla» son los mismos de «Liga por puntos» (standings_rules). */

type Props = {
  value: SpecialScoringConfig | null | undefined;
  onChange: (next: SpecialScoringConfig) => void;
  standingsRules?: StandingsRulesConfig | null;
  onStandingsRulesChange?: (next: StandingsRulesConfig | null) => void;
  scope?: "full" | "exceptions";
  teamPlayActive?: boolean;
  disabled?: boolean;
};

type Option<T extends string> = { value: T; label: string };

function Choice<T extends string>({
  name,
  value,
  options,
  onChange,
  disabled,
}: {
  name: string;
  value: T;
  options: Option<T>[];
  onChange: (next: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className="sp-scoring__choices" role="radiogroup" aria-label={name}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          className={`sp-scoring__choice${value === option.value ? " is-active" : ""}`}
          onClick={() => onChange(option.value)}
          disabled={disabled}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
  disabled,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (next: number) => void;
  disabled?: boolean;
}) {
  return (
    <label className="sp-scoring__num">
      <input
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const next = Number(e.target.value);
          if (e.target.value !== "" && Number.isInteger(next)) onChange(Math.max(min, Math.min(max, next)));
        }}
        disabled={disabled}
        aria-label={label}
      />
      <span>{label}</span>
    </label>
  );
}

function RangeNumber({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: number;
  onChange: (next: number) => void;
  disabled?: boolean;
}) {
  return (
    <input
      type="number"
      inputMode="numeric"
      min={0}
      max={99}
      value={Number.isFinite(value) ? value : ""}
      onChange={(e) => {
        const next = Number(e.target.value);
        if (e.target.value !== "" && Number.isInteger(next)) onChange(Math.max(0, Math.min(99, next)));
      }}
      disabled={disabled}
      aria-label={label}
    />
  );
}

/** Tabla «Por rangos» del super tiebreak: según los puntos del perdedor, el
 * set cuenta ganador X – perdedor Y juegos. El último rango queda abierto. */
function SuperTiebreakRanges({
  ranges,
  onChange,
  disabled,
}: {
  ranges: SuperTiebreakRange[];
  onChange: (next: SuperTiebreakRange[]) => void;
  disabled?: boolean;
}) {
  const isExample = JSON.stringify(ranges) === JSON.stringify(EXAMPLE_STB_RANGES);

  function update(index: number, patch: Partial<SuperTiebreakRange>) {
    const next = ranges.map((r, i) => (i === index ? { ...r, ...patch } : { ...r }));
    // «a» de un rango empuja el «de» del siguiente: sin huecos ni traslapes.
    if (patch.max !== undefined && patch.max !== null && next[index + 1]) next[index + 1].min = patch.max + 1;
    onChange(next);
  }

  function add() {
    if (ranges.length >= MAX_STB_RANGES) return;
    const last = ranges[ranges.length - 1];
    const lastMin = last?.min ?? 0;
    const closed = ranges.map((r, i) => (i === ranges.length - 1 ? { ...r, max: lastMin } : { ...r }));
    onChange([
      ...closed,
      { min: lastMin + 1, max: null, winner_games: last?.winner_games ?? 6, loser_games: last?.loser_games ?? 0 },
    ]);
  }

  function remove(index: number) {
    if (ranges.length <= 1) return;
    const next = ranges.filter((_, i) => i !== index).map((r) => ({ ...r }));
    if (index > 0 && index < ranges.length - 1) next[index].min = (next[index - 1].max ?? 0) + 1;
    if (index === 0) next[0].min = 0;
    next[next.length - 1].max = null;
    onChange(next);
  }

  return (
    <div className="sp-scoring__ranges" data-testid="stb-ranges">
      {isExample && <span className="sp-scoring__pill">Ejemplo: ajústalo a tu reglamento</span>}
      <ol>
        {ranges.map((range, index) => {
          const last = index === ranges.length - 1;
          const n = index + 1;
          return (
            <li key={index} className="sp-scoring__range">
              <span className="sp-scoring__range-text">
                Si el perdedor anota de
                <RangeNumber label={`Rango ${n}: de`} value={range.min} onChange={(v) => update(index, { min: v })} disabled={disabled} />
                {last ? (
                  <span className="sp-scoring__range-open">o más</span>
                ) : (
                  <>
                    a
                    <RangeNumber label={`Rango ${n}: a`} value={range.max ?? range.min} onChange={(v) => update(index, { max: v })} disabled={disabled} />
                  </>
                )}
                puntos
              </span>
              <span className="sp-scoring__range-text">
                → ganador
                <RangeNumber label={`Rango ${n}: juegos del ganador`} value={range.winner_games} onChange={(v) => update(index, { winner_games: v })} disabled={disabled} />
                juegos, perdedor
                <RangeNumber label={`Rango ${n}: juegos del perdedor`} value={range.loser_games} onChange={(v) => update(index, { loser_games: v })} disabled={disabled} />
                juegos
              </span>
              <button
                type="button"
                className="sp-scoring__range-remove"
                onClick={() => remove(index)}
                disabled={disabled || ranges.length <= 1}
                aria-label={`Quitar rango ${n}`}
              >
                Quitar
              </button>
            </li>
          );
        })}
      </ol>
      <button type="button" className="sp-scoring__range-add" onClick={add} disabled={disabled || ranges.length >= MAX_STB_RANGES}>
        + Agregar rango
      </button>
      <span className="sp-scoring__help">
        El set del super tiebreak cuenta para quien lo ganó, con esos juegos. El último rango cubre «o más» puntos.
      </span>
    </div>
  );
}

export default function SpecialScoringSection({
  value,
  onChange,
  standingsRules,
  onStandingsRulesChange,
  scope = "full",
  teamPlayActive = false,
  disabled = false,
}: Props) {
  const [advanced, setAdvanced] = useState(false);
  const scoring = normalizeSpecialScoring(value);
  const full = scope === "full" && onStandingsRulesChange !== undefined;
  const pointsOn = full && standingsRulesKind(standingsRules) === "points";
  const points = pointsOn ? standingsRules?.points ?? DEFAULT_STANDINGS_POINTS : null;
  const unchanged = isDefaultSpecialScoring(scoring) && !pointsOn;
  const error = specialScoringError(scoring);

  function patch(next: Partial<SpecialScoringConfig>) {
    onChange({ ...scoring, ...next });
  }

  function patchPoints(next: Partial<typeof DEFAULT_STANDINGS_POINTS>) {
    onStandingsRulesChange?.(pointsLeagueStandingsRules({ ...(points ?? DEFAULT_STANDINGS_POINTS), ...next }));
  }

  const example = specialScoringExample(scoring, points);

  return (
    <div className="format-form-section form__full sp-scoring" data-testid="special-scoring">
      <div className="format-form-section__header sp-scoring__head">
        <h3 className="hint-label">
          Puntuación especial
          <FieldHint>Cuánto vale cada resultado en la tabla. Primero defines esto y luego cómo se desempata.</FieldHint>
        </h3>
        <span className={`sp-scoring__badge${unchanged ? "" : " is-custom"}`}>
          {unchanged ? "Sin cambios: como hoy" : "Personalizada"}
        </span>
      </div>
      <p className="sp-scoring__note">
        Lo que ves marcado es <strong>cómo cuenta hoy</strong>. Si no tocas nada, la tabla se calcula igual que siempre.
      </p>

      <div className="sp-scoring__block">
        <strong>Victoria por W.O. (no se presentó) vale</strong>
        <Choice
          name="Victoria por W.O. vale"
          value={scoring.walkover_value}
          options={[
            { value: "one_game", label: "1 juego, 0 sets (actual)" },
            { value: "score", label: "Marcador elegido" },
            { value: "win_only", label: "Solo victoria (sin sets ni juegos)" },
          ]}
          onChange={(next) =>
            patch({ walkover_value: next, walkover_score: next === "score" ? scoring.walkover_score ?? DEFAULT_CHOSEN_SCORE : null })
          }
          disabled={disabled}
        />
        {scoring.walkover_value === "score" && (
          <label className="sp-scoring__score">
            Marcador:
            <input
              type="text"
              value={scoring.walkover_score ?? ""}
              onChange={(e) => patch({ walkover_score: e.target.value })}
              disabled={disabled}
              aria-label="Marcador del W.O."
              placeholder="6-0 6-0"
            />
          </label>
        )}
        <span className="sp-scoring__help">
          Con «Marcador elegido» el ganador suma esos sets y juegos para los desempates. «Solo victoria» cuenta el triunfo
          (y sus puntos) sin sumar sets ni juegos: no entra en los %.
        </span>
      </div>

      <div className="sp-scoring__block">
        <strong>DEF (descalificado) sin marcador vale</strong>
        {teamPlayActive ? (
          <span className="sp-scoring__help">
            En categorías por equipos se define con la casilla «cuenta como jugado» de la sección Por equipos.
          </span>
        ) : (
          <>
            <Choice
              name="DEF sin marcador vale"
              value={scoring.defaulted_value}
              options={[
                { value: "same_as_walkover", label: "Igual que W.O. (actual)" },
                { value: "score", label: "Marcador elegido" },
                { value: "win_only", label: "Solo victoria (sin sets ni juegos)" },
              ]}
              onChange={(next) =>
                patch({ defaulted_value: next, defaulted_score: next === "score" ? scoring.defaulted_score ?? DEFAULT_CHOSEN_SCORE : null })
              }
              disabled={disabled}
            />
            {scoring.defaulted_value === "score" && (
              <label className="sp-scoring__score">
                Marcador:
                <input
                  type="text"
                  value={scoring.defaulted_score ?? ""}
                  onChange={(e) => patch({ defaulted_score: e.target.value })}
                  disabled={disabled}
                  aria-label="Marcador del DEF"
                  placeholder="6-0 6-0"
                />
              </label>
            )}
          </>
        )}
      </div>

      <div className="sp-scoring__block">
        <strong>RET (se retiró a medio partido) vale</strong>
        <Choice
          name="RET vale"
          value={scoring.retired_mode}
          options={[
            { value: "keep", label: "Dejar el marcador tal cual (actual)" },
            { value: "complete", label: "Completar sets faltantes a favor del ganador" },
            { value: "score", label: "Marcador elegido" },
            { value: "win_only", label: "Solo victoria (sin sets ni juegos)" },
          ]}
          onChange={(next) =>
            patch({ retired_mode: next, retired_score: next === "score" ? scoring.retired_score ?? DEFAULT_CHOSEN_SCORE : null })
          }
          disabled={disabled}
        />
        {scoring.retired_mode === "score" && (
          <label className="sp-scoring__score">
            Marcador:
            <input
              type="text"
              value={scoring.retired_score ?? ""}
              onChange={(e) => patch({ retired_score: e.target.value })}
              disabled={disabled}
              aria-label="Marcador del RET"
              placeholder="6-0 6-0"
            />
          </label>
        )}
        <span className="sp-scoring__help">Ej. 6-3 2-1 RET → completar = 6-3 6-1. Sigue contando como derrota jugada para quien se retiró.</span>
      </div>

      {full && (
        <div className="sp-scoring__block">
          <strong>Puntos en la tabla</strong>
          <Choice
            name="Puntos en la tabla"
            value={pointsOn ? "points" : "none"}
            options={[
              { value: "none", label: "Sin puntos — ordena por victorias (actual)" },
              { value: "points", label: "Con puntos" },
            ]}
            onChange={(next) => {
              if (next === "points" && !pointsOn) {
                onStandingsRulesChange?.(pointsLeagueStandingsRules(standingsRules?.points ?? DEFAULT_STANDINGS_POINTS));
              } else if (next === "none" && pointsOn) {
                onStandingsRulesChange?.(itfStandingsRules());
              }
            }}
            disabled={disabled}
          />
          {pointsOn && points && (
            <div className="sp-scoring__nums">
              <NumberField label="victoria" value={points.win} min={1} max={99} onChange={(n) => patchPoints({ win: n })} disabled={disabled} />
              <NumberField label="derrota jugada" value={points.loss_completed} min={0} max={99} onChange={(n) => patchPoints({ loss_completed: n })} disabled={disabled} />
              <NumberField label="derrota por W.O." value={points.walkover_loss} min={0} max={99} onChange={(n) => patchPoints({ walkover_loss: n })} disabled={disabled} />
            </div>
          )}
          <span className="sp-scoring__help">Son los mismos números de «Liga por puntos» en Criterios de desempate.</span>
        </div>
      )}

      <button
        type="button"
        className="sp-scoring__toggle"
        aria-expanded={advanced}
        onClick={() => setAdvanced((open) => !open)}
      >
        Opciones avanzadas {advanced ? "▴" : "▾"}
      </button>

      {advanced && (
        <div className="sp-scoring__advanced" data-testid="special-scoring-advanced">
          {full && (
            <div className="sp-scoring__block">
              <strong>
                Puntos extra {!pointsOn && <span className="sp-scoring__pill">Solo con «Con puntos»</span>}
              </strong>
              <div className="sp-scoring__nums">
                <NumberField
                  label="derrota por W.O./DEF (puede ser −1)"
                  value={scoring.walkover_loss_points ?? points?.walkover_loss ?? 0}
                  min={-99}
                  max={99}
                  onChange={(n) => patch({ walkover_loss_points: n })}
                  disabled={disabled || !pointsOn}
                />
                <NumberField
                  label="victoria por W.O."
                  value={scoring.walkover_win_points ?? points?.win ?? 3}
                  min={0}
                  max={99}
                  onChange={(n) => patch({ walkover_win_points: n })}
                  disabled={disabled || !pointsOn}
                />
                <NumberField
                  label="por cada set ganado 6-0"
                  value={scoring.bonus_bagel_set}
                  min={0}
                  max={9}
                  onChange={(n) => patch({ bonus_bagel_set: n })}
                  disabled={disabled || !pointsOn}
                />
                <NumberField
                  label="por ganar sin perder set"
                  value={scoring.bonus_straight_sets}
                  min={0}
                  max={9}
                  onChange={(n) => patch({ bonus_straight_sets: n })}
                  disabled={disabled || !pointsOn}
                />
                <NumberField
                  label="por set ganado en derrota"
                  value={scoring.bonus_set_in_loss}
                  min={0}
                  max={9}
                  onChange={(n) => patch({ bonus_set_in_loss: n })}
                  disabled={disabled || !pointsOn}
                />
              </div>
              <span className="sp-scoring__help">Todos en 0 = sin bonus (actual).</span>
            </div>
          )}

          <div className="sp-scoring__block">
            <strong>¿W.O. y DEF cuentan en % de sets y juegos para desempatar?</strong>
            <Choice
              name="W.O. y DEF en los porcentajes"
              value={scoring.exceptions_count_in_pcts ? "yes" : "no"}
              options={[
                { value: "yes", label: "Sí, con su marcador (actual)" },
                { value: "no", label: "No, se ignoran en los %" },
              ]}
              onChange={(next) => patch({ exceptions_count_in_pcts: next === "yes" })}
              disabled={disabled}
            />
          </div>

          {full && (
            <div className="sp-scoring__block">
              <strong>Super tiebreak (10 puntos) cuenta como</strong>
              <Choice
                name="Super tiebreak"
                value={scoring.super_tiebreak_value}
                options={[
                  { value: "one_game", label: "1 juego (actual)" },
                  { value: "points", label: "Sus puntos, ej. 10-7 = 10 juegos" },
                  { value: "ranges", label: "Por rangos" },
                ]}
                onChange={(next) =>
                  patch({
                    super_tiebreak_value: next,
                    super_tiebreak_ranges:
                      next === "ranges" ? scoring.super_tiebreak_ranges ?? EXAMPLE_STB_RANGES.map((r) => ({ ...r })) : null,
                  })
                }
                disabled={disabled}
              />
              {scoring.super_tiebreak_value === "ranges" && (
                <SuperTiebreakRanges
                  ranges={scoring.super_tiebreak_ranges ?? EXAMPLE_STB_RANGES}
                  onChange={(next) => patch({ super_tiebreak_ranges: next })}
                  disabled={disabled}
                />
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}

      {full && (
        <div className="sp-scoring__example" data-testid="special-scoring-example">
          <span className="sp-scoring__eyebrow">Así cuenta · ejemplo en vivo</span>
          <span className="sp-scoring__headline">{unchanged ? "Como hoy" : `Si eliges: ${exampleHeadline(scoring, points)}`}</span>
          <ul>
            {example.map((row) => (
              <li key={row.result}>
                <span>{row.result}</span>
                <strong>{row.detail}</strong>
              </li>
            ))}
          </ul>
        </div>
      )}
      {scope === "exceptions" && (
        <span className="sp-scoring__help">En escalera solo cambian W.O., DEF y RET: el marcador con el que cuentan.</span>
      )}
    </div>
  );
}
